import { faixa } from "./formato.ts";

type Apres = { rotulo: string; mgMl?: number; uMl?: number };
type DoseBase = { min?: number; max?: number; un?: string; apres?: Apres[]; semDose?: boolean; via?: string; intervalo?: string };

export type CalculoDose = {
  lo: number;
  hi: number;
  /** Unidade do total: "mg", "µg/h", "U". */
  base: string;
  /** Sufixo de tempo da infusão: "", "/h", "/min". */
  suf: string;
  /** Volume por apresentação, quando a concentração tem a mesma base. */
  vols: { rotulo: string; lo: number; hi: number }[];
};

/** Dose total pelo peso. Só vale para unidades "por kg". */
export function doseCalc(d: DoseBase, peso: number | null | undefined): CalculoDose | null {
  const un = d.un || "";
  if (d.min == null || !peso || !un.includes("/kg")) return null;
  const res = un.replace("/kg", "");
  const base = res.split("/")[0]!;
  const suf = res.slice(base.length);
  const lo = d.min * peso;
  const hi = (d.max ?? d.min) * peso;
  const vols = (d.apres || []).flatMap((p) => {
    let conc: number | null = null;
    if (p.mgMl && base === "mg") conc = p.mgMl;
    else if (p.mgMl && base === "µg") conc = p.mgMl * 1000;
    else if (p.uMl && base === "U") conc = p.uMl;
    return conc ? [{ rotulo: p.rotulo, lo: lo / conc, hi: hi / conc }] : [];
  });
  return { lo, hi, base: res, suf, vols };
}

export type ResultadoDose =
  | { tipo: "fixa"; lo: number; hi?: number; un: string }
  | { tipo: "peso"; quando: "por hora" | "por minuto" | "por dose"; calc: CalculoDose }
  | { tipo: "sem-base" };

/** O que a ficha de dose mostra para um peso: total por animal, total pelo peso, ou nada a calcular. */
export function resultadoDose(d: DoseBase, peso: number | null | undefined): ResultadoDose | null {
  if (d.semDose) return null;
  const un = d.un || "";
  if (/\/(gato|cão|bezerro|animal)$/.test(un) && d.min != null) return { tipo: "fixa", lo: d.min, hi: d.max, un: un.split("/")[0]! };
  const calc = doseCalc(d, peso);
  if (!calc) return { tipo: "sem-base" };
  const quando = calc.suf === "/h" ? "por hora" : calc.suf === "/min" ? "por minuto" : "por dose";
  return { tipo: "peso", quando, calc };
}

/** Texto da posologia ao levar a dose para a receita. */
export function posologia(d: DoseBase, peso: number | null | undefined) {
  const c = doseCalc(d, peso);
  const ref = `${faixa(d.min ?? 0, d.max, 3)} ${d.un ?? ""}`;
  const dose = c ? `${faixa(c.lo, c.hi, 3)} ${c.base} (${ref})` : ref;
  return `${dose}${d.via ? `, ${d.via}` : ""}${d.intervalo ? `, ${d.intervalo}` : ""}.`;
}
